import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Converter } from "opencc-js/cn2t";
import type { Tool } from "./types";

export type Locale = "zh-CN" | "zh-TW" | "en";

const STORAGE_KEY = "xm-web-language-v1";
const toTraditional = Converter({ from: "cn", to: "tw" });

function traditionalize(source: string): string {
  return toTraditional(source)
    .replaceAll("賬號", "帳號")
    .replaceAll("登錄", "登入")
    .replaceAll("用戶名", "使用者名稱")
    .replaceAll("軟件", "軟體")
    .replaceAll("視頻", "影片")
    .replaceAll("打印", "列印")
    .replaceAll("信息", "資訊");
}

const EN: Record<string, string> = {
  "小明生活工具箱": "Xiaoming Life Toolbox",
  "全部工具": "All tools", "登录 / 注册": "Sign in / Register", "登录": "Sign in", "注册": "Register",
  "生活里的小问题，": "Life's little problems,", "在这里解决。": "solved right here.", "找到你需要的工具": "Find the tool you need",
  "搜索工具、用途或分类": "Search tools, uses, or categories", "全部": "All", "取消收藏": "Remove favorite", "收藏": "Favorite",
  "没有找到匹配的工具": "No matching tools found", "换个关键词或分类试试。": "Try another keyword or category.",
  "返回全部工具": "Back to all tools", "返回工具箱": "Back to toolbox", "这里没有这个工具": "This tool doesn't exist",
  "收银台": "Checkout", "正在创建收银台…": "Creating checkout…", "付款页面加载失败": "Unable to load checkout",
  "请使用支付宝扫码付款": "Scan with Alipay to pay", "等待支付结果…": "Waiting for payment…", "支付宝付款二维码": "Alipay payment QR code",
  "支付成功": "Payment successful", "支付失败": "Payment failed", "订单已过期": "Order expired", "已退款": "Refunded", "正在确认支付": "Confirming payment",
  "推荐与合作": "Recommendations & partnerships", "广告": "Ad", "好物推荐": "Product pick", "品牌合作": "Brand partnership", "服务推荐": "Service pick", "支持项目": "Support this project",
  "用户名": "Username", "密码": "Password", "重复密码": "Confirm password", "登录账号": "Sign in", "创建账号": "Create account",
  "注册并登录": "Register and sign in", "请稍候…": "Please wait…", "进入工具箱": "Open toolbox", "退出登录": "Sign out",
  "你已登录。": "You are signed in.", "密码需为 6–128 位英文字母或数字": "Password must be 6–128 letters or numbers",
  "两次输入的密码不一致": "Passwords do not match", "请求失败": "Request failed", "操作失败": "Operation failed",
  "显示": "Show ", "隐藏": "Hide ", "需要购买后使用": " requires purchase", "立即购买": "Buy now", "该收费项目暂未上架": "This paid item is not available yet",
  "计算表达式": "Expression", "计算结果": "Calculate", "结果": "Result", "计算失败": "Calculation failed",
  "文字内容": "Text", "清理空行": "Remove empty lines", "行去重": "Remove duplicate lines", "英文大写": "Uppercase", "英文小写": "Lowercase", "复制": "Copy",
  "字符": "characters", "不含空格": "without spaces", "行": "lines", "词": "words",
  "候选项（每行一个）": "Options (one per line)", "今天就吃这个": "Pick this for me", "随机结果": "Random choice",
  "点击开始，替你做决定": "Click to let us decide", "请先添加候选项": "Add at least one option",
  "每月收入": "Monthly income", "固定支出": "Fixed expenses", "日常支出": "Daily expenses", "其他支出": "Other expenses",
  "月结余": "Monthly balance", "储蓄率": "Savings rate", "元": "CNY", "原始金额": "Original amount", "自定义汇率": "Custom rate", "换算结果": "Converted amount",
  "搜索": "Search", "输入名称、地区或关键词": "Enter a name, area, or keyword", "打开官网": "Official website",
  "全局搜索": "Search all tools", "工具名称或用途": "Tool name or purpose", "我的收藏": "My favorites", "移除": "Remove", "还没有收藏工具": "No favorites yet",
  "选择视频": "Choose video", "提取当前画面": "Capture current frame", "下载画面": "Download frame",
  "选择图片": "Choose image", "识别文字": "Extract text", "识别结果": "Recognition result", "复制文字": "Copy text",
  "选择多张图片": "Choose images", "每帧时长": "Frame duration", "毫秒": "ms", "生成动图": "Create GIF", "下载 GIF": "Download GIF",
  "内容": "Content", "转换并下载 HTML": "Convert and download HTML", "打印内容": "Print content", "打印": "Print",
  "上一题": "Previous", "下一题": "Next", "查看推荐": "View recommendations", "重新测评": "Retake assessment", "匹配度": "Match",
  "手机": "phone", "电脑": "computer", "需要次数": "Runs needed", "预计时间": "Estimated time", "分钟": "minutes",
  "每日目标热量": "Daily calorie target", "餐次": "Meal", "千卡": "kcal", "克": "g", "本餐热量 / 目标": "Meal calories / target", "蛋白质 · 碳水 · 脂肪": "Protein · carbs · fat",
  "税前月薪": "Monthly gross salary", "社保公积金": "Social insurance & housing fund", "专项附加扣除": "Additional deductions", "月度个人所得税": "Monthly income tax", "税后到手": "Net income",
  "商品价格": "Item price", "现有存款": "Current savings", "每月必要支出": "Essential monthly expenses", "可以买": "Safe to buy", "建议再等等": "Better to wait",
  "月收入": "Monthly income", "月租金": "Monthly rent", "其他生活费": "Other living costs", "通勤费用": "Commute costs", "每月结余": "Monthly balance", "租金收入比": "Rent-to-income ratio",
  "供热面积": "Heated area", "每平方米费用": "Cost per m²", "供热季费用": "Heating-season cost",
  "报名人数": "Applicants", "招聘人数": "Openings", "预计参考率": "Expected attendance", "报名竞争比": "Applicant ratio", "参考竞争比": "Attendance ratio",
  "气温": "Temperature", "降雨概率": "Rain chance", "风力": "Wind level", "穿搭建议": "Outfit advice",
  "原价": "Original price", "折扣比例": "Discount", "满减金额": "Promotion reduction", "实付": "Final price", "节省": "Savings",
  "计划周期": "Plan duration", "每天投入": "Daily time", "总预算": "Total budget", "每日预算": "Daily budget", "总投入时间": "Total time",
  "当前进度": "Current progress", "目标进度": "Target", "每天可完成": "Daily progress", "预计还需": "Estimated remaining",
  "单价": "Unit price", "单件变动成本": "Variable cost per item", "每天销量": "Daily sales", "每月固定成本": "Monthly fixed costs", "保本销量": "Break-even sales", "预计利润": "Estimated profit",
  "质量": "Quality", "输出格式": "Output format", "生成的二维码": "Generated QR code", "二维码生成失败": "QR code generation failed",
  "第": "Question", "题": "", "优势": "Strengths", "优先提升": "Improve first", "分": "points",
  "小时": "hours", "天": "days", "年": "years", "件": "items", "级": "level", "英寸": "inches",
  "生活": "Life", "服务": "Services", "产业链": "Industry chain", "可线上办理": "Online available", "线下办理": "In person",
  "难度": "Difficulty", "提前": "", "放票": " release", "主要家乡来源": "Main home regions", "笔试": "Written exam", "面试": "Interview", "报名": "Registration", "考试": "Exam",
};

const CATEGORY_EN: Record<string, string> = {
  "编码扫码": "Codes & scanning", "高频工具": "Popular tools", "果粉专区": "Apple tools", "媒体处理": "Media tools",
  "生活查询": "Life & lookup", "收入赚钱": "Income & business", "游戏工具": "Game tools", "职场学业": "Work & study",
};

const TOOL_EN: Record<string, [string, string]> = {
  "code-tools": ["QR Codes", "Create, batch, scan, and count QR codes"],
  "route:pages/common/index": ["Quick Access", "Search and open frequently used tools"],
  "today-eat": ["What to Eat", "Random picker with editable options"],
  "route:pages/tools/index": ["Tool Directory", "One place for every feature"],
  "budget": ["Smart Budget", "Income, expenses, and risk calculator"],
  "love-assessment": ["Relationship Assessment", "Evaluate appearance, emotional support, income, and energy"],
  "wallet-wait": ["Wallet, Please Wait", "Purchase check with a 24-hour cooling-off period"],
  "preferences": ["My Tools", "View and manage favorite tools"],
  "nutrition": ["Meal Planner", "Calculate food portions from nutrition targets"],
  "mac-assessment": ["Mac Finder", "Find the Mac that fits you best"],
  "iphone-assessment": ["iPhone Finder", "Find the iPhone that fits you best"],
  "apple-stores": ["Apple Stores", "Find nearby Apple Stores"],
  "apple-new-products": ["New Apple Releases", "Track and buy the latest Apple products"],
  "gif-maker": ["GIF Maker", "Combine multiple images into a GIF"],
  "bluetooth-printer": ["Bluetooth Printing", "Connect to nearby printers and print text or images"],
  "video-extract": ["Video Extractor", "Extract videos and captions, with or without watermarks"],
  "ocr-extract": ["Image OCR", "Extract text and export to Word, PDF, or spreadsheets"],
  "image-convert": ["Image Converter", "Batch convert PNG and JPG images"],
  "doc-convert": ["Document Converter", "Convert PDF to Word and Word to PDF"],
  "route:pages/stall/index": ["Street Stall", "Add this page to quick access"],
  "route:pages/guard/index": ["Security Work", "Add this page to quick access"],
  "route:pages/cleaner/index": ["Cleaning Work", "Add this page to quick access"],
  "route:pages/nanny/index": ["Caregiving Work", "Add this page to quick access"],
  "industry-analysis": ["Industry Analysis", "Map supply chains and screen listed companies"],
  "query-center": ["Lookup Center", "Grouped shortcuts and frequent searches"],
  "city-living-cost": ["City Living Costs", "Compare costs across 100 cities in China and abroad"],
  "route:pages/ecom/index": ["E-commerce", "Add this page to quick access"],
  "housing-rank": ["Housing Rankings", "Rank unit price, total price, hospitals, and supermarkets"],
  "route:pages/advanced-search/index": ["Advanced Search", "Add this page to quick access"],
  "route:packages/housing/advanced-search/index": ["Advanced Search", "Search with more detailed filters"],
  "exam-calendar": ["Civil Service Calendar", "Registration, written exam, and interview dates"],
  "heat-grid": ["Heating Lookup", "Find heating providers and service areas"],
  "value-helper": ["Value Estimator", "Describe a project to estimate its value"],
  "hongguo-clips": ["Funny Drama Clips", "Find memorable short-drama scenes"],
  "exchange-rate": ["Currency Converter", "Common currencies and custom global currency codes"],
  "hometown-restaurant-village": ["Regional Restaurants", "Find areas with the most hometown-cuisine restaurants"],
  "route:pages/site/index": ["Site Entry", "Add this page to quick access"],
  "holiday-scenic-reservation": ["Attraction Reservations", "Holiday reservation schedules across China"],
  "route:pages/junzhi/index": ["Military Civilian Jobs", "Add this page to quick access"],
  "route:pages/shop/index": ["Open a Shop", "Add this page to quick access"],
  "route:pages/kaobian/index": ["Public Institution Exams", "Add this page to quick access"],
  "route:pages/kaogong/index": ["Civil Service Exams", "Add this page to quick access"],
  "cert-calendar": ["Certification Calendar", "Compare qualification dates and fees"],
  "visa-policy": ["Visa Policy Finder", "Filter official policies by region, route, and difficulty"],
  "route:pages/global-search/index": ["Global Search", "Search and open any page"],
  "mountain-query": ["Mountain Finder", "Elevation, slope, weather, snow, and province data"],
  "endorsement-machines": ["Shenzhen Endorsement Kiosks", "Find nearby self-service travel endorsement kiosks"],
  "living-query": ["Living Search", "Filter homes, commutes, and daily-life needs"],
  "institution-competition": ["Public Job Competition", "Compare competition by country or province"],
  "route:pages/ride-hailing/index": ["Ride-hailing Work", "Add this page to quick access"],
  "route:pages/search-entry/index": ["WeChat Search", "Popular searches for income, tax, housing, and assessments"],
  "hong-kong-services": ["Hong Kong Services", "Banks, brokers, insurance, transit, SIMs, and stores"],
  "hk-account-opening": ["Hong Kong Accounts", "Filter account requirements, documents, and steps"],
  "route:pages/home/index": ["Location Guide", "Living-location and commute advice"],
  "outfit-advisor": ["Outfit Advisor", "Suggestions by season, weather, occasion, fabric, and color"],
  "campus-visit": ["Campus Visits", "University access rules, booking steps, and official links"],
  "payment-deals": ["Payment Deals", "Latest bank and payment-platform promotions"],
  "route:packages/housing/index/index": ["Rental Filter", "Filter by metro, commute, and rent"],
  "route:pages/index/index": ["Rental Search", "Add this page to quick access"],
  "stall-assessment": ["Street Stall Assessment", "Evaluate location, products, hours, and pricing"],
  "restaurant-assessment": ["Restaurant Assessment", "Cross-check products and candidate locations"],
  "hsp-guide": ["Highly Sensitive Person Guide", "Income and life guidance for low-energy people"],
  "parttime-apps": ["Part-time Platforms", "Organize channels and your investment plan"],
  "restaurant-startup-guide": ["Restaurant Startup Guide", "Six-stage checklist, break-even point, and stop-loss rules"],
  "route:pages/courier/index": ["Courier Work", "Add this page to quick access"],
  "rider-location-assessment": ["Delivery Areas", "Recommend major-city areas by income, rent, and work style"],
  "route:pages/rider-tool/index": ["Delivery Rider", "Add this page to quick access"],
  "income-models": ["Income Models", "Delivery, ride-hailing, stalls, e-commerce, and content"],
  "tax-income": ["Net Income Calculator", "Income tax, annual bonus, and gross/net conversion"],
  "delivery-assessment": ["Delivery Work Assessment", "Step-by-step fit assessment for delivery work"],
  "route:pages/rider/index": ["Make Money", "Add this page to quick access"],
  "money-assessment": ["Income Assessment", "Recommend stable or long-term options from your finances"],
  "selfmedia": ["Content Business", "Content monetization and topic ideas"],
  "game-speed-fortnite": ["Fortnite", "BR progression and efficiency calculators"],
  "game-speed-pubg-mobile": ["PUBG Mobile", "BR progression and efficiency calculators"],
  "game-speed-block-blast": ["Block Blast", "Casual-game progression and efficiency calculators"],
  "game-speed-clash-royale": ["Clash Royale", "Card-game progression and efficiency calculators"],
  "game-speed-zombie-fire": ["Zombie Fire", "Shooter progress and time calculator"],
  "game-speed-zenless-zone-zero": ["Zenless Zone Zero", "Action-RPG progression and efficiency calculators"],
  "game-speed-thunder-fighter": ["Thunder Fighter", "Shoot-'em-up progress and time calculator"],
  "game-speed-roblox": ["Roblox", "UGC-game progression and efficiency calculators"],
  "game-speed-candy-crush": ["Candy Crush", "Puzzle-game progression and efficiency calculators"],
  "game-speed-honor-of-kings": ["Honor of Kings", "MOBA progression and efficiency calculators"],
  "game-speed-minecraft": ["Minecraft", "Sandbox progression and efficiency calculators"],
  "game-speed-whiteout-survival": ["Whiteout Survival", "Survival-strategy progression and efficiency calculators"],
  "game-speed-salted-fish-king": ["King of Salted Fish", "Idle-game progression and efficiency calculators"],
  "game-speed-star-rail": ["Honkai: Star Rail", "Turn-based progression and efficiency calculators"],
  "game-speed-xundao-daqian": ["Xundao Daqian", "Idle cultivation progression and efficiency calculators"],
  "game-speed-genshin": ["Genshin Impact", "Open-world progression and efficiency calculators"],
  "game-speed-free-fire": ["Free Fire", "BR progression and efficiency calculators"],
  "game-speed-mobile-legends": ["Mobile Legends", "MOBA progression and efficiency calculators"],
  "major-assessment": ["College Application", "Cities, universities, majors, and application dates"],
  "software-nav": ["Software Guide", "Find software by task and use case"],
  "gaokao-volunteer-calendar": ["Application Calendar", "Province-specific opening and deadline dates"],
  "subject-assessment": ["Subject Assessment", "Match majors first, then review subject requirements"],
  "ai-coding": ["AI Coding", "Codex, Cursor, and other AI coding assistants"],
};

function englishize(source: string): string {
  if (!source) return source;
  if (EN[source]) return EN[source];
  let text = source;
  const replacements = Object.entries(EN).sort((a, b) => b[0].length - a[0].length);
  for (const [zh, en] of replacements) text = text.split(zh).join(en);
  return text
    .replace(/第\s*(\d+)\s*\/\s*(\d+)\s*题/g, "Question $1 of $2")
    .replace(/已选择\s*(\d+)\s*张图片/g, "$1 images selected")
    .replace(/识别中\s*(\d+)%/g, "Recognizing $1%")
    .replace(/你好，(.+)/, "Hello, $1")
    .replace(/购买后剩余\s*([\d,.]+)\s*CNY/g, "$1 CNY left after purchase")
    .replace(/安全资金还差\s*([\d,.]+)\s*CNY/g, "$1 CNY short of your safety reserve")
    .replace(/([\d,.]+)\s*CNY/g, "$1 CNY");
}

export function translate(source: string, locale: Locale): string {
  if (locale === "zh-CN") return source;
  if (locale === "zh-TW") return traditionalize(source);
  return englishize(source);
}

export function localizeTool(tool: Tool, locale: Locale): Tool {
  if (locale === "zh-CN") return tool;
  if (locale === "zh-TW") return { ...tool, title: traditionalize(tool.title), subtitle: traditionalize(tool.subtitle), categoryName: traditionalize(tool.categoryName) };
  const copy = TOOL_EN[tool.id];
  return { ...tool, title: copy?.[0] || englishize(tool.title), subtitle: copy?.[1] || englishize(tool.subtitle), categoryName: CATEGORY_EN[tool.categoryName] || englishize(tool.categoryName) };
}

type I18nContextValue = { locale: Locale; setLocale: (locale: Locale) => void; t: (source: string) => string; tool: (tool: Tool) => Tool };
const I18nContext = createContext<I18nContextValue | null>(null);

function initialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "zh-CN" || saved === "zh-TW" || saved === "en") return saved;
  } catch { /* use browser preference */ }
  const language = navigator.language.toLowerCase();
  if (language.startsWith("zh-tw") || language.startsWith("zh-hk") || language.startsWith("zh-mo") || language.includes("hant")) return "zh-TW";
  if (!language.startsWith("zh")) return "en";
  return "zh-CN";
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);
  const setLocale = (next: Locale) => {
    setLocaleState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* storage unavailable */ }
  };
  useEffect(() => {
    document.documentElement.lang = locale;
    document.title = locale === "en" ? "Xiaoming Life Toolbox" : locale === "zh-TW" ? "小明生活工具箱" : "小明生活工具箱";
  }, [locale]);
  const value = useMemo<I18nContextValue>(() => ({ locale, setLocale, t: (source) => translate(source, locale), tool: (item) => localizeTool(item, locale) }), [locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

const originalText = new WeakMap<Text, string>();
const renderedText = new WeakMap<Text, string>();
const originalAttributes = new WeakMap<Element, Map<string, string>>();
const TRANSLATED_ATTRIBUTES = ["placeholder", "aria-label", "title"] as const;

function translateDom(root: HTMLElement, locale: Locale): void {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node = walker.nextNode() as Text | null;
  while (node) {
    if (!node.parentElement?.closest("[data-no-auto-translate]") && node.nodeValue?.trim()) {
      const current = node.nodeValue;
      const lastRendered = renderedText.get(node);
      if (!originalText.has(node) || current !== lastRendered) originalText.set(node, current);
      const translated = translate(originalText.get(node)!, locale);
      renderedText.set(node, translated);
      if (current !== translated) node.nodeValue = translated;
    }
    node = walker.nextNode() as Text | null;
  }
  root.querySelectorAll<HTMLElement>("input, textarea, [placeholder], [aria-label], [title]").forEach((element) => {
    if (element.closest("[data-no-auto-translate]")) return;
    let attributes = originalAttributes.get(element);
    if (!attributes) { attributes = new Map(); originalAttributes.set(element, attributes); }
    for (const name of TRANSLATED_ATTRIBUTES) {
      const value = element.getAttribute(name);
      if (!value) continue;
      if (!attributes.has(name)) attributes.set(name, value);
      element.setAttribute(name, translate(attributes.get(name)!, locale));
    }
  });
}

export function LocalizedContent({ children }: { children: ReactNode }) {
  const { locale } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    translateDom(root, locale);
    const observer = new MutationObserver(() => translateDom(root, locale));
    observer.observe(root, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [locale]);
  return <div className="localized-app" ref={ref}>{children}</div>;
}

export function LanguageSwitcher() {
  const { locale, setLocale } = useI18n();
  return (
    <label className="language-switcher" data-no-auto-translate>
      <span className="sr-only">Language</span>
      <select aria-label="Language" value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
        <option value="zh-CN">简体中文</option>
        <option value="zh-TW">繁體中文</option>
        <option value="en">English</option>
      </select>
    </label>
  );
}
